import React, { useContext, useEffect, useState } from "react";
import {
	Box,
	Button,
	Typography,
	Divider,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	useMediaQuery,
	lighten,
	darken,
	getLuminance,
	alpha,
	CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ConnectionsClient from "../../services/ConnectionsClient";
import { useTranslation } from "react-i18next";
import { CreatorContext } from "../../contexts/CreatorContext";

function Services() {
	const { t } = useTranslation();
	const { creator, creatorCategories, creatorServices } =
		useContext(CreatorContext);

	const [categoriesLoading, setCategoriesLoading] = useState(true);
	const [servicesLoading, setServiceesLoading] = useState(false);
	const [servicesData, setServicesData] = useState([]);
	const [categories, setCategories] = useState([]);

	const [selectedCategory, setSelectedCategory] = useState(null);
	const [selectedService, setSelectedService] = useState(null);

	const [selectedOptions, setSelectedOptions] = useState(null);
	const navigate = useNavigate();
	const { basePath } = useParams();
	const isSmallScreen = useMediaQuery("(max-width:800px)"); // Detectar si estamos en una pantalla pequeña

	const getDurationRange = (service) => {
		if (!service.serviceOptions) return `${service.duration} ${t("minutes")}`; // Si no hay opciones, solo mostramos la duración del servicio

		const durations = service.serviceOptions.map((option) => option.duration);
		const minDuration = Math.min(...durations);
		const maxDuration = Math.max(...durations);

		if (minDuration === maxDuration) {
			return `${minDuration} ${t("minutes")}`; // Si todas las duraciones son iguales, mostramos una única duración
		}

		return `${minDuration} - ${maxDuration} ${t("minutes")}`; // Mostrar el rango de duraciones
	};

	// Cambiar categoría principal
	const handleCategoryChange = (categoryId) => {
		setSelectedCategory(categoryId);
		setSelectedService(null);
		setSelectedOptions(null);
	};

	useEffect(() => {
		const fetchCategories = async () => {
			setCategoriesLoading(true);
			try {
				let response = [];

				if (!creator.isCreator) {
					response = await ConnectionsClient.get("/categories", {
						params: {
							/*appId: "58931d28-295b-4ba7-9f5a-4b6d0627d459"*/
						},
					});
				} else {
					response.data = [...creatorCategories];
				}
				console.log(creatorCategories);
				setCategories(response.data);
				if (response.data.length > 0) {
					setSelectedCategory(response.data[0].id);
				} else {
					setSelectedCategory(-1);
				}
			} catch (error) {
				console.error(t("fetchServicesError"), error);
			}
			setCategoriesLoading(false);
		};

		fetchCategories();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const fetchServices = async () => {
			setServiceesLoading(true);
			try {
				let response = [];

				if (!creator.isCreator) {
					response = await ConnectionsClient.get("/services", {
						params: {
							//appId: "58931d28-295b-4ba7-9f5a-4b6d0627d459",
							categoryId: selectedCategory === -1 ? null : selectedCategory,
						},
					});
				} else {
					response.data = [
						...creatorServices.filter(
							(category) => category.categoryId === selectedCategory
						),
					];
				}
				setServicesData(response.data);
			} catch (error) {
				console.error(t("fetchServicesError"), error);
			}
			setServiceesLoading(false);
		};
		if (selectedCategory !== null) {
			fetchServices();
		}
	}, [creator.isCreator, creatorServices, selectedCategory, t]);

	// Selección de servicio
	const handleServiceSelect = (service) => {
		setSelectedService(service); // Establece el servicio seleccionado
		setSelectedOptions(null);
	};

	// Selección de opciones de servicio
	const handleOptionSelect = (option) => {
		console.log(t("optionSelected"), option);
		setSelectedOptions(option);
	};

	// Navegar a la pantalla de resumen cuando el usuario haga clic en "Siguiente"
	const handleNext = (service) => {
		// Verificar que tanto el servicio como la opción estén seleccionados

		// Navegar a la pantalla de resumen
		navigate(`/${basePath}/schedule`, {
			state: { selectedService: service || selectedService, selectedOptions },
		});
	};

	const getFormattedOptionPrice = (option) => {
		if (option.discount && option.discount > 0) {
			const discountedPrice =
				option.price - (option.price * option.discount) / 100;
			return (
				<>
					{/* Precio original tachado */}
					<span
						style={{
							textDecoration: "line-through",
							color: "gray",
							marginRight: "8px",
						}}
					>
						{new Intl.NumberFormat(creator.language ?? "en", {
							style: "currency",
							currency: creator.currency ?? "eur",
						}).format(option.price)}
					</span>
					{/* Precio con descuento en rojo */}
					<span style={{ color: "red", fontWeight: "bold" }}>
						{new Intl.NumberFormat(creator.language ?? "en", {
							style: "currency",
							currency: creator.currency ?? "eur",
						}).format(discountedPrice)}
					</span>
				</>
			);
		}

		// Si no hay descuento, devuelve solo el precio
		return new Intl.NumberFormat(creator.language ?? "en", {
			style: "currency",
			currency: creator.currency ?? "eur",
		}).format(option.price);
	};

	const getPriceRange = (service) => {
		if (!service.serviceOptions) return "";

		const prices = service.serviceOptions.map((option) => {
			// Si hay un descuento, aplicar el descuento
			if (option.discount && option.discount > 0) {
				return option.price - (option.price * option.discount) / 100;
			}
			return option.price;
		});

		const minPrice = Math.min(...prices);
		const maxPrice = Math.max(...prices);

		// Si el precio mínimo y máximo son iguales, devuelve solo el valor
		if (minPrice === maxPrice) {
			return new Intl.NumberFormat(creator.language ?? "en", {
				style: "currency",
				currency: creator.currency ?? "eur",
			}).format(minPrice);
		}

		// Si son diferentes, devuelve el rango de precios
		return `${new Intl.NumberFormat(creator.language ?? "en", {
			style: "currency",
			currency: creator.currency ?? "eur",
		}).format(minPrice)} - ${new Intl.NumberFormat(creator.language ?? "en", {
			style: "currency",
			currency: creator.currency ?? "eur",
		}).format(maxPrice)}`;
	};

	// Función para formatear el precio individual o mostrar el rango de precios
	const getFormattedPrice = (service) => {
		// Si hay múltiples opciones, muestra el rango de precios
		if (service.serviceOptions && service.serviceOptions.length > 0) {
			return getPriceRange(service);
		}

		// Si no hay opciones y hay un descuento
		if (service.discount && service.discount > 0) {
			const discountedPrice =
				service.price - (service.price * service.discount) / 100;
			return (
				<>
					{/* Precio original tachado */}
					<span
						style={{
							textDecoration: "line-through",
							color: "gray",
							marginRight: "8px",
						}}
					>
						{new Intl.NumberFormat(creator.language ?? "en", {
							style: "currency",
							currency: creator.currency ?? "eur",
						}).format(service.price)}
					</span>
					{/* Precio con descuento en rojo */}
					<span style={{ color: "red", fontWeight: "bold" }}>
						{new Intl.NumberFormat(creator.language ?? "en", {
							style: "currency",
							currency: creator.currency ?? "eur",
						}).format(discountedPrice)}
					</span>
				</>
			);
		}

		// Si no hay descuento, muestra solo el precio
		return new Intl.NumberFormat(creator.language ?? "en", {
			style: "currency",
			currency: creator.currency ?? "eur",
		}).format(service.price);
	};

	// Si categories loading
	if (categoriesLoading) {
		return (
			<Box
				sx={{
					padding: "20px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<CircularProgress size={12} />
			</Box>
		);
	}

	// Si no hay categorías disponibles, no renderizamos la lista de servicios
	if (!categories) {
		return (
			<Box sx={{ padding: "20px" }}>
				<Typography variant="h6" fontSize={"14px"}>
					{t("noServicesAvailable")}
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 90px)", // ocupa la pantalla completa
				backgroundColor: creator?.backgroundColor || "#ffffff",
				color: creator?.fontColor || "#000000",
				overflow: "hidden", // evita scroll global
			}}
		>
			<Typography
				variant="h4"
				gutterBottom
				sx={{
					fontSize: "16px",
					fontWeight: 600,
					marginBottom: 2,
					marginLeft: selectedCategory === -1 ? "0px" : "10px",
					textAlign: selectedCategory === -1 ? "center" : "left",
				}}
			>
				{t("selectService")}
			</Typography>

			{/* Tabs de Categorías Principales */}
			{!categoriesLoading && categories.length > 0 && (
				<Box sx={{ width: "100%" }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-start",
							marginBottom: "0px",
							paddingLeft: "8px",
							paddingBottom: "20px",
							overflowX: "auto",
							//whiteSpace: "nowrap",
							scrollbarWidth: "thin",

							flexWrap: "wrap", // 👈 esto hace que pasen a otra línea

							gap: "10px", // separa vertical y horizontalmente
						}}
						id="categories-container"
					>
						{categories.map((category) => (
							<Box
								key={category.id}
								onClick={(e) => {
									// Solo hacer scroll si el contenedor no está envuelto (escritorio)
									const container = e.currentTarget.closest(
										"#categories-container"
									);
									const isWrapped = window.innerWidth < 768; // 👈 ajusta breakpoint si quieres

									if (container && !isWrapped) {
										container.scrollTo({
											left:
												e.currentTarget.offsetLeft -
												container.offsetWidth / 2 +
												e.currentTarget.offsetWidth / 2,
											behavior: "smooth",
										});
									}

									handleCategoryChange(category.id);
								}}
								sx={{
									justifyContent: "center",
									alignItems: "center",
									display: "flex",
									padding: "4px 8px",
									marginRight: "8px",
									height: 30,
									textAlign: "center",
									borderRadius: "6px",
									textTransform: "none",
									cursor: "pointer",
									border: "1px solid",
									borderColor:
										creator?.buttonStyle === "buttonStyle2"
											? "transparent"
											: selectedCategory === category.id
											? creator?.mainColor ?? "#43BD98"
											: (getLuminance(creator?.backgroundColor ?? "#FFFFFF") >=
													0.5
													? lighten
													: darken)(creator?.fontColor ?? "#000000", 0.6),
									color:
										selectedCategory === category.id
											? creator?.mainColor ?? "#43BD98"
											: creator?.fontColor ?? "#000000",
									"&:hover": {
										borderColor: creator?.mainColor ?? "#43BD98",
										color: creator?.mainColor ?? "#43BD98",
									},
									transition: "all 0.3s ease",
								}}
							>
								<Typography
									sx={{
										fontSize: 14,
										fontWeight:
											selectedCategory === category.id ? 600 : "normal",
									}}
								>
									{category.title}
								</Typography>
							</Box>
						))}
					</Box>
				</Box>
			)}

			{/* Contenedor que ajusta si hay opciones de servicio o no */}
			<Box
				sx={{
					display: "flex",
					flex: 1, // ocupa todo el espacio vertical restante
					flexDirection:
						!isSmallScreen &&
						selectedService?.serviceOptions &&
						selectedService?.serviceOptions.length > 0
							? "row"
							: "column",
					gap: "12px",
					overflow: "hidden", // evita scroll extra en el grid/flex
				}}
			>
				{/* Lista de Servicios */}
				{servicesLoading ? (
					<Box
						sx={{
							padding: "20px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<CircularProgress size={12} />
					</Box>
				) : selectedCategory === -1 ? (
					servicesData.length === 0 ? (
						<Box sx={{ margin: 2 }}>
							<Typography variant="h6" fontSize={"14px"}>
								{t("noServicesAvailableForCategory")}
							</Typography>
						</Box>
					) : (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								textAlign: "center",
								marginTop: 4,
								borderRadius: "20px",
								boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
								backgroundColor:
									getLuminance(creator?.backgroundColor ?? "#FFFFFF") >= 0.5
										? darken(creator?.backgroundColor ?? "#FFFFFF", 0.015)
										: lighten(creator?.backgroundColor ?? "#FFFFFF", 0.03),
								maxWidth: 420,
								marginX: "auto",
								cursor: "pointer",
								overflow: "hidden",
								transition: "all 0.3s ease",
								"&:hover": {
									transform: "translateY(-4px)",
									boxShadow: "0 16px 40px rgba(0,0,0,0.1)",
								},
							}}
							onClick={() => {
								setSelectedService(servicesData[0]);
								handleNext(servicesData[0]);
							}}
						>
							{/* Imagen del servicio */}
							{servicesData[0].media.length > 0 &&
								servicesData[0].media[0].url && (
									<Box
										component="img"
										src={servicesData[0].media[0].url}
										alt={servicesData[0].title}
										sx={{
											width: "100%",
											height: 220,
											objectFit: "cover",
											marginBottom: 2,
										}}
									/>
								)}
							<Box sx={{ padding: 3 }}>
								{/* Título */}
								<Typography
									variant="h6"
									sx={{
										fontWeight: 700,
										fontSize: "20px",
										color: creator?.fontColor ?? "#000000",
										marginBottom: 1,
									}}
								>
									{servicesData[0].title}
								</Typography>

								{/* Subtítulo / Descripción breve */}
								<Typography
									variant="body2"
									sx={{
										color: alpha(creator?.fontColor ?? "#000000", 0.7),
										lineHeight: 1.6,
										fontSize: "15px",
										maxWidth: 360,
										marginBottom: 2,
									}}
								>
									{servicesData[0].description}
								</Typography>

								{/* Fila con duración y precio */}
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										gap: 2,
										marginBottom: 2,
									}}
								>
									<Box
										sx={{
											backgroundColor: alpha(
												creator?.mainColor ?? "#43BD98",
												0.08
											),
											color: creator?.mainColor ?? "#43BD98",
											padding: "6px 12px",
											borderRadius: "8px",
											fontWeight: 500,
											fontSize: "14px",
										}}
									>
										{servicesData[0].duration} {t("minutes")}
									</Box>
									<Box
										sx={{
											backgroundColor: alpha(
												creator?.mainColor ?? "#43BD98",
												0.08
											),
											color: creator?.mainColor ?? "#43BD98",
											padding: "6px 12px",
											borderRadius: "8px",
											fontWeight: 600,
											fontSize: "15px",
										}}
									>
										{getFormattedPrice(servicesData[0])}
									</Box>
								</Box>
							</Box>
						</Box>
					)
				) : servicesData && servicesData.length > 0 ? (
					isSmallScreen ? (
						<Box sx={{ paddingBottom: "100px", overflowY: "auto" }}>
							{servicesData.map((service) => (
								<Box key={service.id} sx={{}}>
									<Accordion
										sx={{
											boxShadow: "none", // Elimina las sombras
											"&:before": {
												display: "none", // Elimina la línea divisoria del Accordion
											},
											backgroundColor: creator?.backgroundColor ?? "#FFFFFF",
											color: creator?.fontColor ?? "#000000",
										}}
										expanded={
											!!service.serviceOptions &&
											service.serviceOptions.length > 0
												? undefined
												: false
										}
										/*onClick={() => {
											handleServiceSelect(service);
										}}*/
										/*onChange={(_, expanded) => {
											// Solo permitir que el Accordion se expanda si hay opciones
											if (
												service.serviceOptions &&
												service.serviceOptions.length > 0
											) {
												handleServiceSelect(service);
											}
										}}*/
									>
										<AccordionSummary
											expandIcon={
												service.serviceOptions &&
												service.serviceOptions.length > 0 ? (
													<ExpandMoreIcon
														sx={{
															color: creator?.fontColor ?? "#000000",
														}}
													/>
												) : null
											}
											sx={{
												"& .MuiAccordionSummary-content": {
													justifyContent: "space-between",
													width: "100%",
												},
												padding: { xs: "0px 8px", sm: "0px 16px" },
												height: "70px", // Establece una altura mínima constante
												"&.Mui-expanded": {
													height: "70px", // Asegura que la altura no cambie al expandirse
												},
												backgroundColor:
													selectedService?.id === service.id
														? getLuminance(
																creator?.backgroundColor ?? "#FFFFFF"
														  ) >= 0.5
															? "rgba(0, 0, 0, 0.028)"
															: "rgba(255, 255, 255, 0.15)"
														: "transparent",
												"&:hover": {
													backgroundColor:
														getLuminance(
															creator?.backgroundColor ?? "#FFFFFF"
														) >= 0.5
															? "rgba(0, 0, 0, 0.028)"
															: "rgba(255, 255, 255, 0.15)",
												},
												cursor: "pointer",
											}}
											onClick={() => {
												handleServiceSelect(service);
											}}
										>
											<Box sx={{ width: "calc(100% - 64px)" }}>
												<Typography
													variant="h6"
													sx={{
														fontSize: "16px",
														fontWeight: 500,
														whiteSpace: "nowrap",
														overflow: "hidden",
														textOverflow: "ellipsis",
														maxWidth: "calc(100% - 4px)",
													}}
												>
													{service.title}
												</Typography>
												<Typography
													variant="body2"
													sx={{
														color: alpha(creator?.fontColor ?? "#000000", 0.6),
													}}
												>
													{service.serviceOptions &&
													service.serviceOptions.length > 0
														? getDurationRange(service)
														: `${service.duration} ${t("minutes")}`}
												</Typography>
											</Box>
											{(!service.serviceOptions ||
												(service.serviceOptions &&
													service.serviceOptions.length === 0)) && (
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
													}}
												>
													<Typography
														variant="body1"
														sx={{ fontSize: "16px", fontWeight: 500 }}
													>
														{getFormattedPrice(service)}
													</Typography>
												</Box>
											)}
										</AccordionSummary>
										{service.serviceOptions &&
											service.serviceOptions.length > 0 && (
												<AccordionDetails>
													{/* Opciones del servicio */}
													<Box>
														{service.serviceOptions.map((option) => (
															<Box key={option.id}>
																<Box
																	sx={{
																		cursor: "pointer",
																		display: "flex",
																		justifyContent: "space-between",
																		alignItems: "center",
																		padding: "12px",
																		"&:hover": {
																			color: creator?.mainColor ?? "#43BD98",
																		},
																		color:
																			selectedOptions?.id === option.id
																				? creator?.mainColor ?? "#43BD98"
																				: "inherit",
																	}}
																	onClick={() => handleOptionSelect(option)}
																>
																	<Box>
																		<Typography
																			variant="h6"
																			sx={{
																				fontSize: "16px",
																				fontWeight: 500,
																				color:
																					selectedOptions?.id === option.id
																						? creator?.mainColor ?? "#43BD98"
																						: "inherit",
																			}}
																		>
																			{option.title}
																		</Typography>
																		<Typography
																			variant="body2"
																			sx={{
																				color: alpha(
																					creator?.fontColor ?? "#000000",
																					0.6
																				),
																			}}
																		>
																			{option.duration} {t("minutes")}
																		</Typography>
																	</Box>
																	<Box
																		sx={{
																			display: "flex",
																			alignItems: "center",
																		}}
																	>
																		<Typography
																			variant="body1"
																			sx={{ fontSize: "16px", fontWeight: 500 }}
																		>
																			{getFormattedOptionPrice(option)}
																		</Typography>
																	</Box>
																</Box>
																<Divider
																	sx={{
																		borderColor: alpha(
																			creator?.fontColor ?? "#000000",
																			0.1
																		),
																	}}
																/>
															</Box>
														))}
													</Box>
												</AccordionDetails>
											)}
									</Accordion>
								</Box>
							))}
						</Box>
					) : (
						<Box sx={{ overflowY: "auto" }}>
							{servicesData.map((service) => (
								<Box key={service.id} sx={{}}>
									<Box
										sx={{
											cursor: "pointer",
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											padding: "12px",
											backgroundColor:
												selectedService?.id === service.id
													? getLuminance(
															creator?.backgroundColor ?? "#FFFFFF"
													  ) >= 0.5
														? "rgba(0, 0, 0, 0.028)"
														: "rgba(255, 255, 255, 0.15)"
													: "transparent",
											"&:hover": {
												backgroundColor:
													getLuminance(creator?.backgroundColor ?? "#FFFFFF") >=
													0.5
														? "rgba(0, 0, 0, 0.028)"
														: "rgba(255, 255, 255, 0.15)",
											},
										}}
										onClick={() => handleServiceSelect(service)}
									>
										<Box>
											<Typography
												variant="h6"
												sx={{
													fontSize: "16px",
													fontWeight: 500,
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
													maxWidth: "100%",
												}}
											>
												{service.title}
											</Typography>
											<Typography
												variant="body2"
												sx={{
													color: alpha(creator?.fontColor ?? "#000000", 0.6),
												}}
											>
												{service.serviceOptions &&
												service.serviceOptions.length > 0
													? getDurationRange(service)
													: `${service.duration} ${t("minutes")}`}{" "}
											</Typography>
										</Box>
										<Box
											sx={{
												display: "flex",
												flexDirection: "row",
												justifyContent: "center",
												alignItems: "center",
											}}
										>
											<Typography
												variant="body1"
												sx={{ fontSize: "16px", fontWeight: 500 }}
											>
												{getFormattedPrice(service)}
											</Typography>

											<ArrowForwardIosIcon
												sx={{
													fontSize: "12px",
													marginLeft: "5px",
													verticalAlign: "middle",
													visibility:
														service.serviceOptions &&
														service.serviceOptions.length > 0
															? "visible"
															: "hidden",
												}}
											/>
										</Box>
									</Box>
									<Divider
										sx={{
											borderColor: alpha(creator?.fontColor ?? "#000000", 0.1),
										}}
									/>{" "}
									{/* Línea divisoria entre servicios */}
								</Box>
							))}
						</Box>
					)
				) : (
					<Box sx={{ margin: 2 }}>
						<Typography variant="h6" fontSize={"14px"}>
							{t("noServicesAvailableForCategory")}
						</Typography>
					</Box>
				)}

				{/* Lista de Opciones de Servicio */}
				{!isSmallScreen &&
					selectedService?.serviceOptions &&
					selectedService?.serviceOptions.length > 0 && (
						<Box sx={{ overflowY: "auto" }}>
							{selectedService.serviceOptions.map((option) => {
								return (
									<Box key={option.id}>
										<Box
											sx={{
												cursor: "pointer",
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												padding: "12px",
												"&:hover": { color: creator?.mainColor ?? "#43BD98" },
												color:
													selectedOptions?.id === option.id
														? creator?.mainColor ?? "#43BD98"
														: "inherit",
											}}
											onClick={() => handleOptionSelect(option)}
										>
											<Box>
												<Typography
													variant="h6"
													sx={{
														fontSize: "16px",
														fontWeight: 500,
														color:
															selectedOptions?.id === option.id
																? creator?.mainColor ?? "#43BD98"
																: "inherit",
													}}
												>
													{option.title}
												</Typography>
												<Typography
													variant="body2"
													sx={{
														color: alpha(creator?.fontColor ?? "#000000", 0.6),
													}}
												>
													{option.duration} {t("minutes")}
												</Typography>
											</Box>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
												}}
											>
												<Typography
													variant="body1"
													sx={{ fontSize: "16px", fontWeight: 500 }}
												>
													{getFormattedOptionPrice(option)}
												</Typography>
												{selectedOptions?.id === option.id && (
													<CheckCircleIcon
														sx={{
															color: creator?.mainColor ?? "#43BD98",
															marginLeft: "8px",
														}}
													/>
												)}
											</Box>
										</Box>
										<Divider
											sx={{
												borderColor: alpha(
													creator?.fontColor ?? "#000000",
													0.1
												),
											}}
										/>{" "}
										{/* Línea divisoria entre opciones */}
									</Box>
								);
							})}
						</Box>
					)}
			</Box>

			{/* Botón para ir a la siguiente pantalla */}
			{/* Capa inferior con degradado y botón */}
			{selectedCategory !== -1 && (
				<Box
					sx={{
						position: "fixed",
						bottom: 0,
						left: 0,
						width: "100%",
						background: `linear-gradient(to top, ${
							creator?.backgroundColor || "#000"
						} 85%, rgba(0, 0, 0, 0) 100%)`,
						padding: "28px 20px 24px",
						boxSizing: "border-box",
						textAlign: "right",
						zIndex: 10,
					}}
				>
					<Button
						variant="contained"
						color="primary"
						disabled={
							!selectedService ||
							(selectedService &&
								selectedService.serviceOptions &&
								selectedService.serviceOptions.length > 0 &&
								!selectedOptions)
						}
						onClick={() => handleNext()}
						sx={{
							padding: "10px 28px",
							fontSize: "15px",
							fontWeight: 600,
							borderRadius: "10px",
							boxShadow: "none",
							"&.Mui-disabled": {
								opacity: 0.5,
								color:
									getLuminance(creator?.mainColor ?? "#ffffff") >= 0.5
										? "#000000"
										: "#FFFFFF",
								backgroundColor: creator?.mainColor ?? "#43BD98",
							},
						}}
					>
						{t("next")}
					</Button>
				</Box>
			)}
		</Box>
	);
}

export default Services;
